import { useState } from 'react';
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js"
import { Group, LoadingOverlay, createStyles, Button, Modal, Space, Text, Divider, Alert, useMantineTheme, Loader } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useUserAuth } from '../../contexts/user.context';
import { IconCoins } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useJuneProvider } from '../../contexts/analytics.context';

const useStyles = createStyles((theme) => ({

    innerPaymentDiv: {
        width: '40%',
        flexGrow: 1,
        background: '#f5f5f5',
    },

    paymentButton: {
        width: '100%',
    },

    flexText: {
        display: 'flex',
        width: '100%',
        flexGrow: 1,
    },

    flexTextSpan: {
        fontWeight: 600,
        display: 'flex',
        flex: 1,
        textAlign: 'right',

        // Before
        '&::before': {
            content: '""',
            borderBottom: `1.5px dotted ${theme.colors.gray[5]}`,
            flexGrow: 1,
            display: 'inline-block',
            height: '1px',
            transform: 'translateY(1.05em)',
            margin: '0 0.4em',
        }
    }

}));

const formatPrice = (number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(number);
}

const getPriceForCredits = (numCredits) => {
    const price = numCredits * 0.20;
    return formatPrice(price);
}

const PaymentForm = ({ modalOpen, setModalOpen, numCreditsPurchasing,
    paymentIntentId, setClientSecret, setPaymentIntentId, setIsBuyingCredits, paymentElementFirstLoad, setPaymentElementFirstLoad }) => {

    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [submissionError, setSubmissionError] = useState(false);

    const elements = useElements();
    const stripe = useStripe();

    const { classes } = useStyles();
    const { currentUser, APICreditsAvailable, setAPICreditsAvailable } = useUserAuth();

    const handleError = (error) => {
        setIsProcessingPayment(false);
        setSubmissionError(true);
        const errorMessage = error.message ? error.message : error;
        if (analytics) {
            analytics.track({
                userId: currentUser.uid,
                event: `Failed API credits purchased`,
                properties: {
                    creditsPurchased: numCreditsPurchasing,
                    error: errorMessage,
                }
            });
        }
    }

    const theme = useMantineTheme();
    const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`);

    const analytics = useJuneProvider();

    const paymentHandler = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) return;

        setSubmissionError(false);
        setIsProcessingPayment(true);

        const confirmPaymentData = {
            elements,
            confirmParams: {
                return_url: `${window.location.origin}`,
            },
            redirect: 'if_required'
        };

        const { error } = await stripe.confirmPayment(confirmPaymentData);

        if (error) {
            handleError(error);
            if (analytics) {
                analytics.track({
                    userId: currentUser.uid,
                    event: `Error for API credit purchase`,
                    properties: {
                        creditsPurchased: numCreditsPurchasing,
                        type: "Confirmation error",
                        error: error.message,
                    }
                });
            }
            return;
        }

        const processedPayment = await fetch("/.netlify/functions/processPayment", {
            method: "POST",
            body: JSON.stringify({
                paymentIntentId,
                numCredits: numCreditsPurchasing,
                userId: currentUser.uid,
                email: currentUser.email,
            }),
        });
        const processedPaymentJSON = await processedPayment.json();

        if (processedPaymentJSON.success !== true) {
            handleError('Error processing payment');
            if (analytics) {
                analytics.track({
                    userId: currentUser.uid,
                    event: `Error for API credit purchase`,
                    properties: {
                        creditsPurchased: numCreditsPurchasing,
                        type: "Processed payment was not successful",
                        error: processedPaymentJSON.error,
                    }
                });
            }
        }
        const oldAPICreditsAvailable = APICreditsAvailable;
        setAPICreditsAvailable(APICreditsAvailable + numCreditsPurchasing);
        setIsProcessingPayment(false);
        setIsBuyingCredits(false);
        setModalOpen(false);
        notifications.show({
            title: `Your purchase was successful`,
            message: `You now have ${(oldAPICreditsAvailable + numCreditsPurchasing).toLocaleString()} credits. A receipt will be sent to ${currentUser.email}.`,
            color: "teal",
            style: { top: '60px' },
            autoClose: 5000,
        });
        if (analytics) {
            analytics.track({
                userId: currentUser.uid,
                event: `API credits purchased`,
                properties: {
                    creditsPurchased: numCreditsPurchasing,
                }
            });
        }
    }

    return (
        <Modal
            opened={modalOpen}
            fullScreen={isXs}
            onClose={() => {
                console.log('closing')
                setModalOpen(false)
                setClientSecret(null);
                setPaymentIntentId(null);
                setIsBuyingCredits(false);
                setPaymentElementFirstLoad(false);
            }}
            title={`Buy ${numCreditsPurchasing.toLocaleString()} credits`}
            radius='lg'
            size='md'
            centered
            styles={{
                title: {
                    fontSize: '18px',
                    fontWeight: 600,
                },
            }}
            transitionProps={{ transition: 'fade', duration: 200 }}
        >
            <Text className={classes.flexText}>
                Your current credit balance
                <Text component='span' className={classes.flexTextSpan}>{APICreditsAvailable.toLocaleString()}</Text>
            </Text>
            <Space h='xs' />
            <Text className={classes.flexText}>
                Your new credit balance
                <Text component='span' className={classes.flexTextSpan}>{(APICreditsAvailable + numCreditsPurchasing).toLocaleString()}</Text>
            </Text>
            <Space h='md' />
            <Text weight={600} size='lg'>Order summary (USD)</Text>
            <Space h='sm' />
            <Text className={classes.flexText}>
                Subtotal
                <Text component='span' className={classes.flexTextSpan}>${getPriceForCredits(numCreditsPurchasing)}</Text>
            </Text>
            <Space h='xs' />
            <Text className={classes.flexText}>
                Tax
                <Text component='span' className={classes.flexTextSpan}>$0.00</Text>
            </Text>
            <Space h='xs' />
            <Text className={classes.flexText}>
                Order total
                <Text component='span' className={classes.flexTextSpan}>${getPriceForCredits(numCreditsPurchasing)}</Text>
            </Text>
            <Space h='xl' />
            <Divider />
            <Space h='xl' />
            <div style={{ position: 'relative' }}>
                <LoadingOverlay visible={isProcessingPayment} overlayBlur={2} sx={{ margin: '-5px' }} />
                <form onSubmit={paymentHandler}>
                    <PaymentElement
                        onReady={() => { setPaymentElementFirstLoad(false) }}
                    />
                    {paymentElementFirstLoad && <Group position='center'> <Loader /> </Group>}
                    <Space h='xl' />
                    {
                        submissionError &&
                        <>
                            <Alert title="Oops!" color="red" radius='lg'>
                                There was a problem processing your payment.
                                Please try again or contact us at support@roundtable.ai
                            </Alert>
                            <Space h='xl' />
                        </>

                    }
                    <Button
                        className={classes.paymentButton}
                        disabled={!stripe}
                        radius='md'
                        size='lg'
                        leftIcon={<IconCoins stroke={1.5} size={22} />}
                        type='submit'
                    >
                        {`Buy ${numCreditsPurchasing.toLocaleString()} credits`}
                    </Button>
                </form>
            </div>
        </Modal>
    )
}

export default PaymentForm;